import { Component, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";

import { DEFAULT_INTERRUPTSOURCES, Idle } from "@ng-idle/core";
import { Keepalive } from "@ng-idle/keepalive";
import { Subject, takeUntil } from "rxjs";

import { AccountService } from "@account/services";
import { AppStorage } from "@bbwt/utils/app-storage";
import { SystemConfigurationService, SettingsSectionsName, SettingsSection, SessionSettings } from "@main/system-configuration";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { UserHelperService } from "@main/users/user-helper.service";

@Component({
    selector: "idle-banner",
    templateUrl: "./idle-banner.component.html",
    styleUrls: ["./idle-banner.component.scss"]
})
export class IdleBannerComponent implements OnDestroy {
    private destroyed$ = new Subject<void>();

    showBanner: boolean;
    countDown: number;

    constructor(
        private idle: Idle,
        private keepalive: Keepalive,
        private accountService: AccountService,
        private broadcastService: BroadcastService,
        private userHelperService: UserHelperService,
        private route: ActivatedRoute
    ) {
        this.keepalive.request("api/account/keepalive");
        this.configureIdle();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.keepalive?.stop();
        this.idle?.stop();
    }

    private configureIdle(): void {
        if (this.route.snapshot.data["sysConfig"][SettingsSectionsName.UserSessionSettings]) {
            this.refreshIdle(SessionSettings.parse(this.route.snapshot.data["sysConfig"]));
        }

        this.broadcastService
            .on<SettingsSection>(SystemConfigurationService.SettingsSectionChangedEventName)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(settingsSection => {
                if (settingsSection.sectionName === SettingsSectionsName.UserSessionSettings) {
                    this.refreshIdle(settingsSection.value);
                }
            });

        this.idle.onIdleStart.pipe(takeUntil(this.destroyed$)).subscribe(() => (this.showBanner = true));
        this.idle.onIdleEnd.pipe(takeUntil(this.destroyed$)).subscribe(() => (this.showBanner = false));
        this.idle.onTimeoutWarning.pipe(takeUntil(this.destroyed$)).subscribe(countdown => (this.countDown = countdown));
        this.idle.onTimeout.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            this.showBanner = false;
            AppStorage.setItemForSession(AppStorage.LogoutReasonMessageKey, "You've exceeded idle time. Please login again.");
            AppStorage.setItemForSession(AppStorage.LastVisitedPageUrlKey, location.pathname);
            this.accountService.logout();
        });
    }

    private refreshIdle(sessionSettingsSection: SessionSettings): void {
        if (!sessionSettingsSection) return;

        this.idle.clearInterrupts();
        this.idle.stop();
        this.keepalive.stop();

        const idleTime = this.isGeneric() ? sessionSettingsSection.idleTimeForGenericLogin : sessionSettingsSection.idleTime;

        if (sessionSettingsSection.idleTimeEnabled && idleTime) {
            this.keepalive.interval(this.getKeepaliveIntervalSeconds(idleTime));
            this.idle.setIdle(idleTime * 60 + 1);
            this.idle.setTimeout(60);
            this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
            this.idle.watch();
        }
    }

    private getKeepaliveIntervalSeconds(idleTimeMinutes: number): number {
        const quarterIdleSeconds = Math.floor((idleTimeMinutes * 60) / 4);
        return Math.min(10 * 60, Math.max(60, quarterIdleSeconds));
    }

    private isGeneric() {
        return this.userHelperService.isGenericPatient || this.userHelperService.isGenericLogin;
    }
}
